import { Controller, UseFormReturn } from 'react-hook-form'
import { FormInput } from '../../../../components/forms/input/FormInput'
import { FormSelect } from '../../../../components/forms/select/FormSelect'
import { useRecuperarOpcCatalogoQuery } from '../../../../redux/api'
import { AppCodigosCatalogo, AppConstantes } from '../../../../utils/Constantes'
import { calcularDuracionEnDias } from '../../../../utils/CalculoDuracionUtil'
import { calcularFechaVencimiento } from '../../../../utils/CalculoFechaVencimientoUtil'
import dayjs from '../../../../utils/DayjsUtil'
import { FormReactDatePicker } from '../../../../components/forms/datePicker/FormReactDatePicker'
import { useEffect, useState } from 'react'
import { Alert } from '../../../../components/alerts/Alert'
import { DatosAutorizacionFormRadioBotones } from './DatosAutorizacionFormRadioBotones'
import { Opcion } from '../../../../models/catalogo.type'
import { Autorizacion } from '../../models'
import { DatosAutorizacionFormPorDuracion } from './DatosAutorizacionFormPorDuracion'
import { TAutorizacionForm } from '../../models/autorizacionForm.type'
import { DatosAutorizacionFormPorVencimiento } from './DatosAutorizacionFormPorVencimiento'

type Props = {
  formPagina: UseFormReturn<TAutorizacionForm>
  isPorFechaVencimiento: boolean | undefined
  isAutorizacionIndefinida: boolean | undefined
  autorizacion?: Autorizacion
  setAutorizacionIndefinida: (arg: boolean) => void
  setPorFechaVencimiento: (argo: boolean) => void
  isModoEditar: boolean
  isSuccess: boolean
  usuarioPermiso?: boolean
}
export const DatosAutorizacionForm = ({
  isAutorizacionIndefinida,
  isPorFechaVencimiento,
  setAutorizacionIndefinida,
  setPorFechaVencimiento,
  formPagina: {
    control,
    formState: { errors },
    register,
    getValues,
    setValue,
    clearErrors,
    watch,
  },
  isModoEditar,
  autorizacion,
  isSuccess,
  usuarioPermiso,
}: Props) => {
  // Seccion de captura principal de autorizacion.
  // Este componente sincroniza 3 ramas de UI/validacion:
  // 1) Autorizacion indefinida (sin vencimiento/duracion)
  // 2) Vigencia por fecha de vencimiento
  // 3) Vigencia por duracion (cantidad + unidad)
  // Adicionalmente fuerza defaults (por ejemplo estatus "Vigente" en creacion/indefinida) y
  // calcula la contraparte (fecha<->cantidad) cuando cambian los campos base.
  const { data: catAutorizacion } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.AUTORIZACION
  )
  const { data: catCompetencia } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.COMPETENCIA_AUTORIZACION
  )
  const { data: catEstatusAutorizacion } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.ESTATUS_AUTORIZACION
  )
  const watchEstatus = watch('estatus')

  const [renderizar, setRenderizar] = useState(false)

  const diffFechaUltimaRenovacioFechaVencimiento = dayjs(getValues('fechaVencimiento')).diff(
    dayjs(getValues('fechaUltimaRenovacion'))
  )

  const cantidadAniosPreviosReactDatePicker = 60
  const cantidadAniosPosterioresReactDatePicker = 60

  useEffect(() => {
    // Inicializacion:
    // - En creacion: estatus Vigente por default y rama "indefinida" activa
    // - En edicion: hidrata banderas desde backend (esIndefinida/esPorFechaVencimiento)
    if (!isModoEditar) {
      setValue('estatus', {
        valor: AppConstantes.CVE_ESTATUS_AUTORIZACION_VIGENTE,
        etiqueta: AppConstantes.DESC_ESTATUS_AUTORIZACION_VIGENTE,
      })
      cambiarCamposAutorizacionIndefinida(true, false)
    }
    if (isAutorizacionIndefinida) {
      setValue('estatus', {
        valor: AppConstantes.CVE_ESTATUS_AUTORIZACION_VIGENTE,
        etiqueta: AppConstantes.DESC_ESTATUS_AUTORIZACION_VIGENTE,
      })
    }

    if (isModoEditar && autorizacion) {
      setValue('esPorFechaVencimiento', autorizacion.esPorFechaVencimiento)
      setValue('esIndefinida', autorizacion.esIndefinida)
      setPorFechaVencimiento(autorizacion.esPorFechaVencimiento)
      setAutorizacionIndefinida(autorizacion.esIndefinida)
    }
  }, [isModoEditar, isSuccess])

  /**
   * @description Cambia el estado de isAutorizacionIndefinida cada que se realiza un click
   *              en el checkbox de esIndefinida
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 20/12/2022
   */
  const cambiarCamposAutorizacionIndefinida = (
    valorRadioButtonIndefinida: boolean,
    valorRadioButtonFechaVencimiento: boolean
  ) => {
    // Cambia la rama de captura y limpia campos que quedarían inconsistentes.
    // Nota: al activar "indefinida" se vacia vencimiento/duracion y se quitan errores asociados.
    setPorFechaVencimiento(valorRadioButtonFechaVencimiento)
    setAutorizacionIndefinida(valorRadioButtonIndefinida)
    setValue('esPorFechaVencimiento', valorRadioButtonFechaVencimiento)
    setValue('esIndefinida', valorRadioButtonIndefinida)

    if (isAutorizacionIndefinida) {
      setValue('unidadDuracion', null)
      setValue('cantidadDuracion', null)
      setValue('fechaVencimiento', null)
      clearErrors('fechaVencimiento')
    } else {
      setValue('estatus', {
        valor: AppConstantes.CVE_ESTATUS_AUTORIZACION_VIGENTE,
        etiqueta: AppConstantes.DESC_ESTATUS_AUTORIZACION_VIGENTE,
      })
      setValue('unidadDuracion', null)
      setValue('cantidadDuracion', null)
      clearErrors('estatus')
      clearErrors('unidadDuracion')
      clearErrors('cantidadDuracion')
    }
    if (valorRadioButtonIndefinida) {
      clearErrors('unidadDuracion')
      clearErrors('cantidadDuracion')
      clearErrors('fechaVencimiento')
    }
  }

  /**
   * @description Realiza el calculo de fecha o cantidad cada que ocurre un cambio en
   *              los campos de fecha emision, fecha vencimiento, unidad o cantidad.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 20/12/2022
   */
  const calcularFechaCantidad = () => {
    // Calcula y setea el campo derivado segun el modo de captura.
    // Si es por fecha: calcula cantidad de dias y fuerza unidad=DIAS.
    // Si es por duracion: calcula fecha de vencimiento en base a unidad/cantidad.
    if (isPorFechaVencimiento) {
      setValue('esPorFechaVencimiento', true)
      const cantidadDias = calcularDuracionEnDias(
        getValues('fechaEmision'),
        getValues('fechaVencimiento')
      )
      setValue('cantidadDuracion', cantidadDias)
      setValue('unidadDuracion', {
        valor: AppConstantes.CVE_UNIDAD_AUTORIZACION_RECORDATORIOS_DIAS,
        etiqueta: AppConstantes.DESC_UNIDAD_AUTORIZACION_RECORDATORIOS_DIAS,
      })
      setRenderizar(!renderizar)
    } else {
      setValue('esPorFechaVencimiento', false)
      const fechaVencimiento = calcularFechaVencimiento(
        getValues('fechaEmision'),
        getValues('unidadDuracion')?.valor ?? '',
        getValues('cantidadDuracion')
      )
      setValue('fechaVencimiento', fechaVencimiento)
      setRenderizar(!renderizar)
    }
  }

  /**
   * @description Limpia campo de fecha de vencimiento al cambiar fecha de emisión o al
   *              dejar en blanco fecha de emisión.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 19/01/2023
   * @param e Opción: fecha de emisión seleccionada o null
   */
  const limpiarFechaVencimiento = (e: Date) => {
    // Evita rangos inconsistentes: si el usuario mueve la fecha de emision despues del
    // vencimiento actual, se limpia el vencimiento.
    if (!isAutorizacionIndefinida) {
      if (dayjs(getValues('fechaVencimiento')).diff(dayjs(getValues('fechaEmision'))) < 0) {
        setValue('fechaVencimiento', null)
        setValue('cantidadDuracion', 0)
      }
    }

    if (!e) {
      setValue('fechaVencimiento', null)
    }
  }

  /**
   * @description Limpia el campo de Fecha última renovación cuando la fecha de emisión es menor
   *              o igual a la fecha de emisión de última renovación seleccionada.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 30/03/2023
   */
  const limpiarFechaUltimaRenovacion = () => {
    // Regla UI: la "ultima renovacion" debe ser anterior a "fecha de emision".
    const diffFechaEmisionFechaUltimaRenovacion = dayjs(getValues('fechaEmision')).diff(
      dayjs(getValues('fechaUltimaRenovacion'))
    )
    if (diffFechaEmisionFechaUltimaRenovacion <= 0) {
      setValue('fechaUltimaRenovacion', null)
    }
  }

  return (
    <div className='ms-20 d-flex row'>
      <div className='d-flex'>
        <div className='col-2'>
          <h3 className='me-15'>Datos de la autorización</h3>
        </div>
        <div className='col-10 mb-10'>
          <div className='d-flex'>
            <div className='col-3 mb-7 '>
              <Controller
                control={control}
                name='numero'
                render={() => (
                  <FormInput
                    maxLength={100}
                    isObligatorio={true}
                    nombreCampo='numero'
                    nombre='Número'
                    register={register}
                    errors={errors}
                    placeholder='Ej. 19-VII-10-22'
                    disabled={!usuarioPermiso}
                  />
                )}
              />
            </div>
            <div className='col-7 mb-7 ms-3'>
              <Controller
                control={control}
                name='autorizacion'
                render={({ field: { onChange, value } }) => (
                  <FormSelect
                    nombre='Autorización'
                    nombreCampo='autorizacion'
                    isObligatorio={true}
                    value={value}
                    elementos={catAutorizacion}
                    onChange={(e: Opcion) => {
                      onChange(e)
                    }}
                    placeholder='Ej.Reciclaje de Residuos Peligrosos - Combustible "Formulado Sólidos'
                    errors={errors}
                    isDisabled={!usuarioPermiso}
                  />
                )}
              />
            </div>
          </div>
          <div className='col-12'></div>
          <div className='d-flex'>
            <div className='col-3 mb-7'>
              <Controller
                control={control}
                name='competencia'
                render={({ field: { onChange, value } }) => (
                  <FormSelect
                    nombreCampo='competencia'
                    elementos={catCompetencia}
                    nombre='Competencia'
                    value={value}
                    placeholder='Ej. Municipal'
                    isObligatorio={true}
                    onChange={(e: Opcion) => {
                      onChange(e)
                    }}
                    errors={errors}
                    isDisabled={!usuarioPermiso}
                  />
                )}
              />
            </div>
            <div className='col-5 ms-3 mb-7'>
              <Controller
                control={control}
                name='dependencia'
                render={() => (
                  <FormInput
                    maxLength={50}
                    isObligatorio={true}
                    nombreCampo='dependencia'
                    nombre='Dependencia'
                    register={register}
                    errors={errors}
                    placeholder='Ej. Protección civil'
                    disabled={!usuarioPermiso}
                  />
                )}
              />
            </div>
          </div>

          <div className='d-flex'>
            <div className='col-3'>
              <Controller
                control={control}
                name='estatus'
                render={({ field: { onChange, value } }) => (
                  <FormSelect
                    isDisabled={(!isModoEditar && isAutorizacionIndefinida) || !usuarioPermiso}
                    nombre='Estatus'
                    nombreCampo='estatus'
                    isObligatorio={true}
                    value={value}
                    elementos={
                      !isModoEditar
                        ? catEstatusAutorizacion?.filter(
                            (e) => e?.valor !== AppConstantes.CVE_ESTATUS_AUTORIZACION_BAJA
                          )
                        : isAutorizacionIndefinida
                        ? catEstatusAutorizacion?.filter(
                            (e) => e?.valor !== AppConstantes.CVE_ESTATUS_AUTORIZACION_VENCIDA
                          )
                        : catEstatusAutorizacion
                    }
                    onChange={(e: Opcion) => {
                      onChange(e)
                    }}
                    placeholder='Ej.Vigente'
                    errors={errors}
                  />
                )}
              />
            </div>
            <div className='col-3 ms-3'>
              <div className='mb-7 d-flex flex-column'>
                <label className='form-label fs-4'>
                  Fecha de emisión <span className='text-danger'>*</span>
                </label>

                <Controller
                  control={control}
                  name='fechaEmision'
                  render={({ field: { value, onChange } }) => (
                    <FormReactDatePicker
                      required={true}
                      name='Fecha de emisión '
                      dateFormat='dd/MMM/yyyy'
                      placeholderText='Ej. 22/dic/2022'
                      onChange={(e: Date) => {
                        onChange(e)
                        calcularFechaCantidad()
                        limpiarFechaVencimiento(e)
                        limpiarFechaUltimaRenovacion()
                      }}
                      isClearable={usuarioPermiso}
                      value={value}
                      cantidadAniosPrevios={cantidadAniosPreviosReactDatePicker}
                      cantidadAniosSiguientes={cantidadAniosPosterioresReactDatePicker}
                      disabled={!usuarioPermiso}
                    />
                  )}
                />

                {errors && errors['fechaEmision'] && (
                  <p className='text-danger text-end fs-5'>{errors['fechaEmision']?.message}</p>
                )}
              </div>
            </div>

            <div className='col-5 ms-8'>
              {watchEstatus?.valor === 'BAJA' && (
                <Alert
                  color='danger'
                  icono='fa-solid fa-triangle-exclamation fs-2x'
                  mensaje={`Al actualizar una autorización a 'Baja' todos sus recordatorios serán eliminados y las tareas y condicionantes serán actualizadas a 'Baja'`}
                  titulo={'Autorización en baja'}
                />
              )}
            </div>
          </div>
          {/* {RADIO BOTONES} */}
          <div className={`${!usuarioPermiso && 'overlay overlay-allowed'}`}>
            <DatosAutorizacionFormRadioBotones
              isAutorizacionIndefinida={isAutorizacionIndefinida}
              setAutorizacionIndefinida={setAutorizacionIndefinida}
              isPorFechaVencimiento={isPorFechaVencimiento}
              setPorFechaVencimiento={setPorFechaVencimiento}
              isModoEditar={isModoEditar}
              calcularFechaCantidad={calcularFechaCantidad}
              cambiarCamposAutorizacionIndefinida={cambiarCamposAutorizacionIndefinida}
              {...{
                control,
                errors,
                register,
                getValues,
                setValue,
                clearErrors,
              }}
              usuarioPermiso={usuarioPermiso}></DatosAutorizacionFormRadioBotones>
          </div>
          <div className='col-12'></div>
          {/* {CAMPOS DURACIÓN Y FECHA VENCIMIENTO} */}
          <div
            className={`d-flex ${!isAutorizacionIndefinida && 'mb-5'}`}
            style={isAutorizacionIndefinida ? { display: 'none' } : {}}>
            <DatosAutorizacionFormPorDuracion
              isAutorizacionIndefinida={isAutorizacionIndefinida}
              isPorFechaVencimiento={isPorFechaVencimiento}
              calcularFechaCantidad={calcularFechaCantidad}
              {...{
                control,
                errors,
                register,
                getValues,
              }}
              usuarioPermiso={usuarioPermiso}></DatosAutorizacionFormPorDuracion>

            <DatosAutorizacionFormPorVencimiento
              isAutorizacionIndefinida={isAutorizacionIndefinida}
              isPorFechaVencimiento={isPorFechaVencimiento}
              calcularFechaCantidad={calcularFechaCantidad}
              {...{
                control,
                errors,
                register,
                getValues,
              }}
              usuarioPermiso={usuarioPermiso}></DatosAutorizacionFormPorVencimiento>
          </div>
          <div className='col-12'></div>
          {/* Fecha Renovacion */}
          <div className={`d-flex ${!isAutorizacionIndefinida && 'mt-5'}`}>
            <div className={`col-4 ${!isAutorizacionIndefinida && 'mt-5'}`}>
              <div className=' d-flex flex-column'>
                <label className='form-label fs-4'>
                  Fecha de emisión de última renovación (Opcional){' '}
                </label>
                <Controller
                  control={control}
                  name='fechaUltimaRenovacion'
                  render={({ field: { value, onChange } }) => (
                    <FormReactDatePicker
                      disabled={!usuarioPermiso}
                      maxDate={
                        getValues('fechaEmision')
                          ? new Date(
                              dayjs(getValues('fechaEmision'))
                                .subtract(0, 'days')
                                .format('YYYY-MM-DD')
                            )
                          : undefined
                      }
                      dateFormat='dd/MMM/yyyy'
                      placeholderText='Ej. 22/dic/2022'
                      onChange={onChange}
                      isClearable={usuarioPermiso}
                      value={value}
                    />
                  )}
                />
                {errors && errors['fechaUltimaRenovacion'] && (
                  <p className='text-danger text-end fs-5'>
                    {errors['fechaUltimaRenovacion']?.message}
                  </p>
                )}
              </div>
            </div>
            <div className='col-2'></div>
            <div className='col-4 mt-6 ms-10'>
              {diffFechaUltimaRenovacioFechaVencimiento <= 0 && !isAutorizacionIndefinida && (
                <Alert
                  color='dark'
                  mensaje='Actualiza la fecha de vencimiento con una fecha posterior a la de última renovación.'
                />
              )}

              <Alert
                color='dark'
                mensaje='La fecha de emisión de última renovación debe ser anterior a la fecha de emisión.'
              />
            </div>
          </div>
          <div className='col-12'></div>
        </div>
      </div>
    </div>
  )
}
